import { type ChangeEvent, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import debounce from "lodash/debounce";
import { CADTABLE_DEBOUNCE_TIME } from "~/utils/constants";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";

import { isCADInquiryEnabled } from "~/utils/features";
import { Row } from "~/common/components/Layout";
import DataTable from "~/common/components/Table/DataTable";
import { userIsRegistered } from "~/domains/user/utils/roles";
import { type DropdownEntry } from "~/common/types";
import {
  type CadFormDefinition,
  CadFormFieldType,
  type FieldDefinition,
} from "~/features/productDetails/components/Cad/CadTypes";
import { Alert, type SelectChangeEvent, styled, TextField, Typography } from "~/common/mui";

interface CadTableProps {
  data: CadFormDefinition;
  onChange: (newValue: [string, string]) => void;
}

export const CadForm = ({ data, onChange: onChangeExternal }: CadTableProps) => {
  const { t } = useTranslation();
  const [currentValues, setCurrentValues] = useState<Record<string, string>>({});

  useLayoutEffect(() => {
    setCurrentValues(generateCurrentValues(data));
  }, [data]);

  const onChange = useMemo(() => {
    return debounce(onChangeExternal, CADTABLE_DEBOUNCE_TIME);
  }, [onChangeExternal]);

  const makeFieldProps = (field: FieldDefinition) => {
    return {
      onChange: (value: string | ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | SelectChangeEvent<unknown>) => {
        const newValue = typeof value === "string" ? value : (value.target.value as string);
        const newValues = { ...currentValues, [field.symbol]: newValue };
        setCurrentValues(newValues);
        onChange([field.symbol, newValue]);
        if (field.type === CadFormFieldType.Select) {
          onChange.flush();
        }
      },
      onBlur: () => {
        onChange.flush();
      },
      value: currentValues[field.symbol] ?? "",
    };
  };

  return (
    <>
      {data && data.length > 0 && (
        <Root>
          <Typography variant="headlineSmall" color="text.primary">
            {t("page.pdp.cad.title")}
          </Typography>
          <TableContainer className="col-md-8">
            <DataTable.Table theme={"stripes"}>
              {data.map((field, index) => (
                <DataTable.CellRow key={field.symbol}>
                  <TableCell className={tableCellClassName}>
                    <TableLabelContainer>
                      <Typography variant="bodyLarge" color="text.primary">
                        {field.label}
                      </Typography>
                      <TableLabelHelper variant="bodyLarge" color="text.primary">
                        {getHelperLabel(field)}
                      </TableLabelHelper>
                    </TableLabelContainer>
                  </TableCell>
                  <TableOptionsCell className={tableCellClassName}>
                    {field.type === CadFormFieldType.Select && (
                      <SimpleSelect
                        className={tableDropdownClassName}
                        data-test-id={"scha__cad-table__dropdown" + index}
                        fullWidth={true}
                        values={getOptions(field)}
                        {...makeFieldProps(field)}
                      />
                    )}
                    {field.type === CadFormFieldType.Text && (
                      <Typography variant="bodyLarge" color="text.primary" component="span">
                        {currentValues[field.symbol]}
                      </Typography>
                    )}
                    {field.type === CadFormFieldType.Number && (
                      <TextField
                        fullWidth
                        inputProps={{ "data-test-id": "scha__cad-table__input" + index }}
                        className={"scha__cad-table__input"}
                        autoComplete={"off"}
                        {...makeFieldProps(field)}
                      />
                    )}
                  </TableOptionsCell>
                </DataTable.CellRow>
              ))}
            </DataTable.Table>
            {userIsRegistered() && isCADInquiryEnabled() && (
              <Alert severity={"info"} sx={{ mt: 3.75 }}>
                {t("page.pdp.cad.messageInfo")}
              </Alert>
            )}
          </TableContainer>
        </Root>
      )}
    </>
  );
};

const tableDropdownClassName = "scha__cad-table__dropdown";
const tableCellClassName = "scha__cad-table__cell";

const Root = styled(Row)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: `${theme.spacing(6.25)} !important`,

  [theme.breakpoints.down("desktop-s")]: {
    ["& .scha__cad-table__cell"]: {
      paddingRight: theme.spacing(2),
    },
  },
}));

const TableContainer = styled("div")(({ theme }) => ({
  paddingLeft: "0 !important",
  marginTop: theme.spacing(3.75),
}));

const TableCell = styled(DataTable.Cell)({});

const TableLabelContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  height: "100%",
});

const TableLabelHelper = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(0.5),
}));

const TableOptionsCell = styled(DataTable.Cell)(({ theme }) => ({
  maxWidth: theme.spacing(41),
  paddingRight: theme.spacing(2),

  p: {
    display: "none",
  },

  ["label + .MuiInput-formControl"]: {
    marginTop: "0 !important",
  },

  [`.${tableDropdownClassName}`]: {
    borderBottom: `1px solid ${theme.palette.divider}`,

    ["> div:first-child"]: {
      background: "transparent",
      marginBottom: 0,
    },
  },
}));

function generateCurrentValues(data: CadFormDefinition) {
  const currentValues: Record<string, string> = {};
  for (const item of data) {
    let value;
    if (item.type === CadFormFieldType.Text || item.type === CadFormFieldType.Number) {
      value = item.valuesFieldGrid[0].value;
    } else if (item.type === CadFormFieldType.Select) {
      value = item.valuesFieldGrid.find((ele) => ele.selected === "selected")?.value;
    }
    currentValues[item.symbol] = value ?? "";
  }
  return currentValues;
}

function getHelperLabel(field: FieldDefinition) {
  return field.valuesFieldGrid[0]?.helpLabel;
}

function getOptions(field: FieldDefinition): DropdownEntry<string>[] {
  return field.valuesFieldGrid.map((item) => ({
    label: item.label,
    value: item.value,
  }));
}
